import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { PRODUCTION_WORKSPACE } from '../contexts/workspace.logic';
import { rackEquipmentQueryKey } from './equipmentWrite.logic';
import { confirmedRowRacks } from '../utils/rackName.logic';

export interface RackEquipmentItem {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  mounting_position: string;
  is_zero_u?: boolean;
  weight_kg?: number | null;
  power_w?: number | null;
  serial_number?: string;
  catalog_id?: number | null;
  manufacturer?: string;
  model?: string;
  asset_tag?: string;
  device_role?: string;
  status?: string;
  airflow?: string;
  depth?: string;
  tenant?: string;
  attributes?: any;
}

export function useRackWorkspaceData(actualCode: string, actualRackId: string | undefined, activeWorkspaceId: string = PRODUCTION_WORKSPACE) {
  const queryClient = useQueryClient();

  // 1. Racks List
  const { data: racksList = [], isLoading: isRacksLoading, isError: isRacksError,
    isFetching: isRacksFetching, refetch: refetchRacks } = useQuery({
    queryKey: ['racks', actualCode],
    queryFn: async ({ signal }) => {
      const { data } = await api.get(`/api/datacenters/${actualCode}/racks`, { signal });
      if (!Array.isArray(data) || data.some(item => !item || !Number.isInteger(item.id) || item.id <= 0)) throw new Error('Invalid rack list');
      return data;
    },
    enabled: !!actualCode,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // The navigation list also supplies facts: revalidate it on every rack change.
  useEffect(() => {
    if (actualCode && actualRackId) void refetchRacks();
  }, [actualCode, actualRackId, refetchRacks]);

  // 3. Equipment List
  const { data: equipmentList = [], isLoading: isEqLoading, isError: isEqError,
    isFetching: isEqFetching } = useQuery<RackEquipmentItem[]>({
    queryKey: [...rackEquipmentQueryKey(actualCode, actualRackId, activeWorkspaceId)],
    queryFn: async ({ signal }) => {
      const { data } = await api.get(`/api/datacenters/${actualCode}/racks/${actualRackId}/equipment?workspace=${activeWorkspaceId}`, { signal });
      if (!Array.isArray(data) || data.some(item => !item || typeof item.id !== 'string' || !item.id)) throw new Error('Invalid equipment list');
      return data;
    },
    enabled: !!actualRackId,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // 4. DC Info
  const { data: dcInfo } = useQuery({
    queryKey: ['dc-info', actualCode],
    queryFn: () => api.get(`/api/datacenters/${actualCode}`).then(res => res.data),
    enabled: !!actualCode,
  });

  // Derived state
  const isLoading = isEqLoading || isRacksLoading;
  const currentRack = useMemo(() => racksList.find((r: any) => String(r.id) === String(actualRackId)) || null, [racksList, actualRackId]);
  const slots = currentRack?.u_slots ?? 0;

  // Adjacent racks for navigation
  const { prevRack, nextRack } = useMemo(() => {
    if (!currentRack || !racksList.length) return { prevRack: null, nextRack: null };
    const sameRow = confirmedRowRacks(racksList, currentRack);
    const idx = sameRow.findIndex((r: any) => String(r.id) === String(actualRackId));
    return {
      prevRack: idx > 0 ? sameRow[idx - 1] : null,
      nextRack: idx >= 0 && idx < sameRow.length - 1 ? sameRow[idx + 1] : null,
    };
  }, [racksList, currentRack, actualRackId]);

  return {
    racksList,
    equipmentList,
    dcInfo,
    isLoading,
    isRefreshing: isRacksFetching || isEqFetching,
    isError: isRacksError || isEqError,
    currentRack,
    slots,
    prevRack,
    nextRack,
    queryClient
  };
}
